'use client';

import type { SegmentAnalytics } from '@posthog/types';
import posthog from 'posthog-js';
import { PostHogProvider } from 'posthog-js/react';
import { useEffect } from 'react';

const posthogKey = process.env.NEXT_PUBLIC_POSTHOG_KEY || '';
const posthogHost = process.env.NEXT_PUBLIC_POSTHOG_HOST || 'https://us.i.posthog.com';
let hasInitializedPosthog = false;

const posthogOptions = {
  api_host: posthogHost,
  person_profiles: 'identified_only' as const,
  capture_pageview: false,
  capture_pageleave: true,
};

function initPosthogStandalone(reason: string) {
  if (hasInitializedPosthog) return;
  hasInitializedPosthog = true;

  posthog.init(posthogKey, posthogOptions);
}

/**
 * Attaches an onerror handler to the Segment CDN script tag if it exists.
 * Some ad blockers (not all) fire onerror when blocking the download.
 */
function onSegmentScriptError(callback: () => void) {
  const script = document.querySelector('script[src*="cdn.segment.com"]');
  if (script) {
    script.addEventListener('error', callback);
  }
}

function usePosthogInit() {
  useEffect(() => {
    if (!posthogKey || hasInitializedPosthog) {
      return;
    }

    let segmentReadyFired = false;

    const initializeWithSegment = () => {
      const analytics = (window as any).analytics;

      if (!analytics?.ready || !analytics?.user) {
        return false;
      }

      analytics.ready(() => {
        segmentReadyFired = true;
        if (hasInitializedPosthog) return;
        hasInitializedPosthog = true;

        posthog.init(posthogKey, {
          ...posthogOptions,
          segment: window.analytics as unknown as SegmentAnalytics,
        });
      });

      return true;
    };

    const timers: number[] = [];

    const scheduleBlockDetection = () => {
      // Signal 1: onerror on the Segment CDN script tag (instant when supported)
      onSegmentScriptError(() => initPosthogStandalone('script onerror'));

      // Signal 2: if analytics.ready() hasn't fired within 10s, Segment is
      // blocked. This is long enough to avoid false positives on slow connections.
      const fallbackId = window.setTimeout(() => {
        if (!segmentReadyFired) {
          initPosthogStandalone('timeout — Segment likely blocked');
        }
      }, 10000);
      timers.push(fallbackId);
    };

    // Try immediately
    const segmentFound = initializeWithSegment();

    if (segmentFound) {
      scheduleBlockDetection();
      return () => {
        timers.forEach((id) => window.clearTimeout(id));
      };
    }

    // Segment stub not found yet — poll for it, but also schedule
    // block detection in case the stub itself never appears
    scheduleBlockDetection();

    const intervalId = window.setInterval(() => {
      if (initializeWithSegment()) {
        window.clearInterval(intervalId);
      }
    }, 250);

    return () => {
      window.clearInterval(intervalId);
      timers.forEach((id) => window.clearTimeout(id));
    };
  }, []);
}

export default function PHProvider({ children }: { children: React.ReactNode }) {
  usePosthogInit();

  if (typeof window === 'undefined' || !posthogKey) {
    return <>{children}</>;
  }
  return <PostHogProvider client={posthog}>{children}</PostHogProvider>;
}
